import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import {
  goalsService,
  type CreateGoalRequest,
  type Goal,
  type GoalSet,
  type GoalStrategyType,
  type GoalType,
  type GoalBmrSettings,
  DEFAULT_GOAL_BMR_SETTINGS,
} from '@/services/goalsService';
import type { GoalWorkoutFuelingTargets } from '@macro-tracker/models';
import { DEFAULT_WORKOUT_FUELING_TARGETS } from '@macro-tracker/models/validators';
import { loadProfileSettings, type UnitSystem } from '@/services/profileSettings';
import { calculateBmr } from '@/services/bmrCalculator';
import { useWeightStore } from '@/stores/weightStore';
import { formatDateInput } from '@/utils/dateTime';

export type { CreateGoalRequest, Goal, GoalSet, GoalBmrSettings };

const DEFAULT_MACRO_PCTS = {
  protein: 40,
  carbs: 40,
  fat: 20,
};

const KG_PER_LB = 0.453592;

const cloneBmrSettings = (settings: GoalBmrSettings): GoalBmrSettings => ({ ...settings });

const cloneWorkoutFuelingTargets = (
  t: GoalWorkoutFuelingTargets,
): GoalWorkoutFuelingTargets => ({
  recovery: { ...t.recovery },
  low: { ...t.low },
  moderate: { ...t.moderate },
  high: { ...t.high },
  race: { ...t.race },
});

const parseNumber = (value: number | string): number | null => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const toDateString = (value: string): string => {
  const trimmed = value.trim();
  if (DATE_ONLY_RE.test(trimmed)) {
    return trimmed;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return formatDateInput();
  }

  return formatDateInput(parsed);
};

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value));
const roundPercent = (value: number): number => Math.round(value * 10) / 10;

export const useGoalsStore = defineStore('goals', () => {
  const weightStore = useWeightStore();
  const unitSystem = ref<UnitSystem>('imperial');
  const tdeeCalories = ref<number | null>(null);
  const activeGoal = ref<Goal | null>(null);
  const goals = ref<Goal[]>([]);
  const goalSets = ref<GoalSet[]>([]);
  const isGoalHistoryLoading = ref(false);
  const effectiveFromDate = ref<string>(formatDateInput());

  const goalType = ref<GoalType>('maintain');
  const weeklyWeightChange = ref<number | string>('');
  const strategyType = ref<GoalStrategyType>('percent');

  const proteinPct = ref(DEFAULT_MACRO_PCTS.protein);
  const carbsPct = ref(DEFAULT_MACRO_PCTS.carbs);
  const fatPct = ref(DEFAULT_MACRO_PCTS.fat);
  const percentWarning = ref(false);

  const currentWeight = ref<number | string>('');
  const useScaledMacros = ref(false);
  const fixedProteinG = ref<number | string>('');
  const fixedCarbsG = ref<number | string>('');
  const fixedFatG = ref<number | string>('');
  const proteinGPerUnit = ref<number | string>('');
  const carbsGPerUnit = ref<number | string>('');
  const fatGPerUnit = ref<number | string>('');

  const baseProteinG = ref<number | string>('');
  const baseCarbsG = ref<number | string>('');
  const baseFatG = ref<number | string>('');
  const flexMacro = ref<'carbs' | 'fat'>('carbs');

  // Embedded BMR settings — stable inputs (no weight). Weight is looked up dynamically
  // from weightStore by effective date.
  const bmrSettings = ref<GoalBmrSettings>(cloneBmrSettings(DEFAULT_GOAL_BMR_SETTINGS));
  const workoutFuelingTargets = ref<GoalWorkoutFuelingTargets>(
    cloneWorkoutFuelingTargets(DEFAULT_WORKOUT_FUELING_TARGETS),
  );

  const isSaving = ref(false);
  const statusMessage = ref('');
  const errorMessage = ref('');
  const savedSnapshot = ref('');

  const takeSnapshot = () => JSON.stringify({
    goalType: goalType.value,
    effectiveFromDate: effectiveFromDate.value,
    weeklyWeightChange: weeklyWeightChange.value,
    strategyType: strategyType.value,
    proteinPct: proteinPct.value,
    carbsPct: carbsPct.value,
    fatPct: fatPct.value,
    currentWeight: currentWeight.value,
    useScaledMacros: useScaledMacros.value,
    fixedProteinG: fixedProteinG.value,
    fixedCarbsG: fixedCarbsG.value,
    fixedFatG: fixedFatG.value,
    proteinGPerUnit: proteinGPerUnit.value,
    carbsGPerUnit: carbsGPerUnit.value,
    fatGPerUnit: fatGPerUnit.value,
    baseProteinG: baseProteinG.value,
    baseCarbsG: baseCarbsG.value,
    baseFatG: baseFatG.value,
    flexMacro: flexMacro.value,
    bmrSettings: bmrSettings.value,
    workoutFuelingTargets: workoutFuelingTargets.value,
  });

  const isDirty = computed(() => savedSnapshot.value !== '' && takeSnapshot() !== savedSnapshot.value);

  const weightUnitLabel = computed(() => (unitSystem.value === 'metric' ? 'kg' : 'lb'));
  const macroUnitLabel = computed(() => (unitSystem.value === 'metric' ? 'g/kg' : 'g/lb'));

  const calorieTarget = computed(() => tdeeCalories.value ?? 0);
  const weeklyChangeValue = computed(() => parseNumber(weeklyWeightChange.value));
  const currentWeightValue = computed(() => parseNumber(currentWeight.value));

  const effectiveWeightEntry = computed(() => weightStore.getWeightForDay(effectiveFromDate.value));
  const effectiveWeightKg = computed(() => effectiveWeightEntry.value?.weightKg ?? null);

  const currentWeightKg = computed(() => {
    if (effectiveWeightKg.value) {
      return effectiveWeightKg.value;
    }

    if (!currentWeightValue.value) {
      return null;
    }
    return unitSystem.value === 'metric'
      ? currentWeightValue.value
      : currentWeightValue.value * KG_PER_LB;
  });

  const gramsPerUnitToKg = (value: number | string): number | null => {
    const parsed = parseNumber(value);
    if (parsed === null) {
      return null;
    }
    return unitSystem.value === 'metric' ? parsed : parsed / KG_PER_LB;
  };

  const gramsPerKgToUnit = (value?: number | null): number | string => {
    if (value === null || value === undefined) {
      return '';
    }
    return unitSystem.value === 'metric' ? value : value * KG_PER_LB;
  };

  const kgToUnit = (value: number): number => (unitSystem.value === 'metric' ? value : value / KG_PER_LB);

  // TDEE = formula(bmrSettings, weight-for-effectiveDate). Manual override short-circuits.
  const calculateTdeeCalories = (): number | null => {
    const bmr = bmrSettings.value;

    if (bmr.useManual && bmr.manualValue) {
      return bmr.manualValue;
    }

    const weightKg = effectiveWeightKg.value;
    if (!weightKg) {
      return null;
    }

    try {
      return calculateBmr({
        weight: weightKg,
        height: bmr.heightCm,
        age: bmr.age,
        gender: bmr.gender,
        activityLevel: bmr.activityLevel,
        formula: bmr.formula,
        highMuscleContent: bmr.highMuscleContent,
      }).tdee;
    } catch {
      return null;
    }
  };

  const refreshWeightDerivedState = (): void => {
    const weightKg = effectiveWeightKg.value;
    currentWeight.value = weightKg ? Math.round(kgToUnit(weightKg) * 10) / 10 : '';
    tdeeCalories.value = calculateTdeeCalories();
  };

  watch([effectiveWeightKg, unitSystem, bmrSettings], refreshWeightDerivedState, { deep: true });

  const macroPercentageTotal = computed(() => proteinPct.value + carbsPct.value + fatPct.value);
  const percentageValid = computed(() => Math.abs(macroPercentageTotal.value - 100) <= 0.1);

  const macroBarSegments = computed(() => {
    const total = macroPercentageTotal.value;
    if (total <= 0) {
      return { protein: 0, carbs: 0, fat: 0 };
    }
    const scale = 100 / total;
    return {
      protein: Math.max(0, proteinPct.value * scale),
      carbs: Math.max(0, carbsPct.value * scale),
      fat: Math.max(0, fatPct.value * scale),
    };
  });

  const weightChangeThresholds = computed(() => {
    const isMetric = unitSystem.value === 'metric';
    return {
      lossMin: isMetric ? 0.11 : 0.25,
      lossMax: isMetric ? 0.9 : 2,
      gainMax: isMetric ? 4.5 : 10,
    };
  });

  const gramsPerKgTargets = computed(() => {
    const protein = gramsPerUnitToKg(proteinGPerUnit.value);
    const carbs = gramsPerUnitToKg(carbsGPerUnit.value);
    const fat = gramsPerUnitToKg(fatGPerUnit.value);
    return {
      protein,
      carbs,
      fat,
    };
  });

  const calculatedMacros = computed(() => {
    if (strategyType.value === 'percent') {
      return {
        protein: (proteinPct.value / 100) * (calorieTarget.value / 4),
        carbs: (carbsPct.value / 100) * (calorieTarget.value / 4),
        fat: (fatPct.value / 100) * (calorieTarget.value / 9),
      };
    }

    if (strategyType.value === 'grams_per_kg') {
      if (useScaledMacros.value) {
        if (!currentWeightKg.value) {
          return null;
        }
        const protein = gramsPerKgTargets.value.protein ?? 0;
        const carbs = gramsPerKgTargets.value.carbs ?? 0;
        const fat = gramsPerKgTargets.value.fat ?? 0;
        return {
          protein: protein * currentWeightKg.value,
          carbs: carbs * currentWeightKg.value,
          fat: fat * currentWeightKg.value,
        };
      }
      return {
        protein: parseNumber(fixedProteinG.value) ?? 0,
        carbs: parseNumber(fixedCarbsG.value) ?? 0,
        fat: parseNumber(fixedFatG.value) ?? 0,
      };
    }

    const proteinBase = parseNumber(baseProteinG.value) ?? 0;
    const carbsBase = parseNumber(baseCarbsG.value) ?? 0;
    const fatBase = parseNumber(baseFatG.value) ?? 0;
    const baseCalories = (proteinBase * 4) + (carbsBase * 4) + (fatBase * 9);
    const remainingCalories = Math.max(0, calorieTarget.value - baseCalories);

    if (flexMacro.value === 'carbs') {
      return {
        protein: proteinBase,
        carbs: carbsBase + (remainingCalories / 4),
        fat: fatBase,
      };
    }

    return {
      protein: proteinBase,
      carbs: carbsBase,
      fat: fatBase + (remainingCalories / 9),
    };
  });

  const previewSummary = computed(() => {
    if (!calorieTarget.value || !calculatedMacros.value) {
      return null;
    }

    if (strategyType.value === 'percent') {
      return `New: ${Math.round(calorieTarget.value)} calories/day (${proteinPct.value}% protein, ${carbsPct.value}% carbs, ${fatPct.value}% fats)`;
    }

    return `New: ${Math.round(calorieTarget.value)} calories/day (P: ${Math.round(calculatedMacros.value.protein)}g, C: ${Math.round(calculatedMacros.value.carbs)}g, F: ${Math.round(calculatedMacros.value.fat)}g)`;
  });

  const currentGoalSummary = computed(() => {
    if (activeGoal.value) {
      return `Current: ${Math.round(activeGoal.value.caloriesTarget)} calories/day`;
    }
    if (calorieTarget.value) {
      return `Current: ${Math.round(calorieTarget.value)} calories/day`;
    }
    return null;
  });

  const goalSetsNewestFirst = computed(() =>
    [...goalSets.value].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate)),
  );

  const goalsAscending = computed(() =>
    [...goals.value].sort((a, b) => toDateString(a.effectiveFromDate).localeCompare(toDateString(b.effectiveFromDate))),
  );

  const getGoalForDay = (day: string = formatDateInput()): Goal | null => {
    for (let index = goalsAscending.value.length - 1; index >= 0; index -= 1) {
      const goal = goalsAscending.value[index];
      if (toDateString(goal.effectiveFromDate) <= day) {
        return goal;
      }
    }

    return null;
  };

  const validationErrors = computed(() => {
    const errors: string[] = [];

    if (!effectiveFromDate.value || Number.isNaN(new Date(effectiveFromDate.value).getTime())) {
      errors.push('Please enter a valid effective date.');
    }

    if (!calorieTarget.value) {
      errors.push('Unable to calculate a calorie target from your BMR settings.');
    }

    const bmr = bmrSettings.value;
    if (!bmr.heightCm || bmr.heightCm <= 0) {
      errors.push('Please enter a valid height.');
    }
    if (bmr.age === null || bmr.age === undefined || bmr.age < 0) {
      errors.push('Please enter a valid age.');
    }

    if (goalType.value === 'lose' || goalType.value === 'gain') {
      if (weeklyChangeValue.value === null || weeklyChangeValue.value <= 0) {
        errors.push('Please enter a positive weekly weight change target.');
      }
    }

    if (strategyType.value === 'percent' && !percentageValid.value) {
      errors.push('Macro percentages must sum to 100%.');
    }

    if (strategyType.value === 'grams_per_kg' && useScaledMacros.value) {
      if (!currentWeightValue.value) {
        errors.push('No weight history found. Log your weight to use scaled macros.');
      }
    }

    if (strategyType.value === 'flex' && !flexMacro.value) {
      errors.push('Please select a macro to flex.');
    }

    return errors;
  });

  const validationWarnings = computed(() => {
    const warnings: string[] = [];

    if ((goalType.value === 'lose' || goalType.value === 'gain') && weeklyChangeValue.value !== null && weeklyChangeValue.value > 0) {
      if (goalType.value === 'lose' && weeklyChangeValue.value > weightChangeThresholds.value.lossMax) {
        warnings.push('Weight change outside recommended range. Consider a more realistic target.');
      }
      if (goalType.value === 'gain' && weeklyChangeValue.value > weightChangeThresholds.value.gainMax) {
        warnings.push('Weight change outside recommended range. Consider a more realistic target.');
      }
      if (goalType.value === 'lose' && weeklyChangeValue.value < weightChangeThresholds.value.lossMin) {
        warnings.push('Weight loss goals below the recommended minimum may be difficult to track.');
      }
    }

    if (calorieTarget.value && calorieTarget.value < 1200) {
      warnings.push('This calorie intake is below recommended minimum. Consider your activity level.');
    }

    if (percentWarning.value) {
      warnings.push('Total macros exceed 100%. Adjust values.');
    }

    if (strategyType.value === 'grams_per_kg') {
      if (useScaledMacros.value) {
        const { protein, carbs, fat } = gramsPerKgTargets.value;
        if ((protein ?? 0) <= 0 || (carbs ?? 0) <= 0 || (fat ?? 0) <= 0) {
          warnings.push('All macros must have a positive value.');
        }
      } else {
        const p = parseNumber(fixedProteinG.value) ?? 0;
        const c = parseNumber(fixedCarbsG.value) ?? 0;
        const f = parseNumber(fixedFatG.value) ?? 0;
        if (p <= 0 || c <= 0 || f <= 0) {
          warnings.push('All macros must have a positive value.');
        }
      }
    }

    if (strategyType.value === 'flex') {
      const proteinBase = parseNumber(baseProteinG.value) ?? 0;
      const carbsBase = parseNumber(baseCarbsG.value) ?? 0;
      const fatBase = parseNumber(baseFatG.value) ?? 0;
      const baseCalories = (proteinBase * 4) + (carbsBase * 4) + (fatBase * 9);
      if (baseCalories > calorieTarget.value) {
        warnings.push('Base amount exceeds your target calories.');
      }
    }

    return warnings;
  });

  const previewError = computed(() => {
    if (validationErrors.value.length > 0 || !previewSummary.value) {
      return 'Please fill in all required fields to generate a preview.';
    }
    return null;
  });

  const isFormValid = computed(() => validationErrors.value.length === 0);

  const normalizePercentages = (changed: 'protein' | 'carbs' | 'fat') => {
    const previousTotal = macroPercentageTotal.value;

    const values = {
      protein: clampPercent(proteinPct.value),
      carbs: clampPercent(carbsPct.value),
      fat: clampPercent(fatPct.value),
    };

    const remaining = 100 - values[changed];
    const others = ['protein', 'carbs', 'fat'].filter((key) => key !== changed) as Array<'protein' | 'carbs' | 'fat'>;
    const otherSum = values[others[0]] + values[others[1]];

    if (remaining <= 0) {
      values[changed] = 100;
      values[others[0]] = 0;
      values[others[1]] = 0;
    } else if (otherSum === 0) {
      values[others[0]] = remaining / 2;
      values[others[1]] = remaining / 2;
    } else {
      values[others[0]] = remaining * (values[others[0]] / otherSum);
      values[others[1]] = remaining * (values[others[1]] / otherSum);
    }

    proteinPct.value = roundPercent(values.protein);
    carbsPct.value = roundPercent(values.carbs);
    fatPct.value = roundPercent(values.fat);

    percentWarning.value = previousTotal > 100;
  };

  const resetMacroDefaults = () => {
    proteinPct.value = DEFAULT_MACRO_PCTS.protein;
    carbsPct.value = DEFAULT_MACRO_PCTS.carbs;
    fatPct.value = DEFAULT_MACRO_PCTS.fat;
  };

  const setGoalType = (value: GoalType) => {
    goalType.value = value;
    if (value === 'fixed' || value === 'maintain') {
      weeklyWeightChange.value = '';
    } else if (value === 'lose' || value === 'gain') {
      // Auto-populate with a sensible default if empty
      const current = parseNumber(weeklyWeightChange.value);
      if (current === null || current <= 0) {
        weeklyWeightChange.value = unitSystem.value === 'metric' ? 0.5 : 1;
      }
    }
  };

  const setStrategyType = (value: GoalStrategyType) => {
    strategyType.value = value;
  };

  const loadActiveGoal = async () => {
    activeGoal.value = await goalsService.getActiveGoal();

    if (!activeGoal.value) {
      // No prior goal — keep current bmrSettings (defaults or what onboarding set).
      return;
    }

    goalType.value = activeGoal.value.goalType;
    effectiveFromDate.value = toDateString(activeGoal.value.effectiveFromDate);
    strategyType.value = activeGoal.value.strategyType;
    bmrSettings.value = cloneBmrSettings(activeGoal.value.bmrSettings ?? DEFAULT_GOAL_BMR_SETTINGS);
    workoutFuelingTargets.value = cloneWorkoutFuelingTargets(
      activeGoal.value.workoutFuelingTargets ?? DEFAULT_WORKOUT_FUELING_TARGETS,
    );

    if (activeGoal.value.strategyType === 'percent') {
      proteinPct.value = activeGoal.value.proteinPct ?? DEFAULT_MACRO_PCTS.protein;
      carbsPct.value = activeGoal.value.carbsPct ?? DEFAULT_MACRO_PCTS.carbs;
      fatPct.value = activeGoal.value.fatPct ?? DEFAULT_MACRO_PCTS.fat;
    }

    if (activeGoal.value.strategyType === 'grams_per_kg') {
      const hasScaledTargets = activeGoal.value.proteinGPerKg != null
        || activeGoal.value.carbsGPerKg != null
        || activeGoal.value.fatGPerKg != null;

      useScaledMacros.value = hasScaledTargets;

      if (hasScaledTargets) {
        proteinGPerUnit.value = gramsPerKgToUnit(activeGoal.value.proteinGPerKg);
        carbsGPerUnit.value = gramsPerKgToUnit(activeGoal.value.carbsGPerKg);
        fatGPerUnit.value = gramsPerKgToUnit(activeGoal.value.fatGPerKg);
        if (calculatedMacros.value) {
          fixedProteinG.value = Math.round(calculatedMacros.value.protein);
          fixedCarbsG.value = Math.round(calculatedMacros.value.carbs);
          fixedFatG.value = Math.round(calculatedMacros.value.fat);
        }
      } else {
        fixedProteinG.value = activeGoal.value.fixedProteinG ?? '';
        fixedCarbsG.value = activeGoal.value.fixedCarbsG ?? '';
        fixedFatG.value = activeGoal.value.fixedFatG ?? '';
      }

    }

    if (activeGoal.value.strategyType === 'flex') {
      baseProteinG.value = activeGoal.value.fixedProteinG ?? '';
      baseCarbsG.value = activeGoal.value.fixedCarbsG ?? '';
      baseFatG.value = activeGoal.value.fixedFatG ?? '';
      flexMacro.value = activeGoal.value.flexMacro ?? 'carbs';
    }
  };

  const loadGoalSets = async (): Promise<GoalSet[]> => {
    isGoalHistoryLoading.value = true;
    try {
      // One-time, idempotent cleanup: collapse any legacy duplicate goals that
      // share an effectiveFromDate so goal resolution can't tie-break to the
      // wrong row. Guarded so partial test mocks of goalsService don't break.
      let dedupedDates: string[] = [];
      if (typeof goalsService.dedupeGoalsByEffectiveDate === 'function') {
        dedupedDates = (await goalsService.dedupeGoalsByEffectiveDate()) ?? [];
      }
      const [loadedGoalSets, loadedGoals] = await Promise.all([
        goalsService.getGoalSets(),
        goalsService.getGoalsHistory(),
      ]);
      goalSets.value = loadedGoalSets;
      goals.value = loadedGoals;

      // If the one-time dedupe collapsed any date's duplicates, the resolved goal
      // for that date may now differ from what its cached diary plan was built
      // on. Re-run the planner from the earliest affected date so those days'
      // targets aren't left stale (unlike a goal save, dedupe deletions don't go
      // through saveGoal's regeneration). Best-effort; resolved lazily to avoid a
      // static circular import with diaryStore, and never fails goal loading.
      if (dedupedDates.length > 0) {
        try {
          const earliest = [...dedupedDates].sort()[0];
          const { useDiaryStore } = await import('@/stores/diaryStore');
          await useDiaryStore().regeneratePlannerForGoalChange(earliest);
        } catch {
          // Swallow: goals are loaded; planner refresh is a downstream convenience.
        }
      }

      return goalSets.value;
    } finally {
      isGoalHistoryLoading.value = false;
    }
  };

  const loadGoalsHistory = async (): Promise<Goal[]> => {
    goals.value = await goalsService.getGoalsHistory();
    return goals.value;
  };

  const updateBmrSettings = (patch: Partial<GoalBmrSettings>): void => {
    bmrSettings.value = { ...bmrSettings.value, ...patch };
  };

  const saveGoal = async (): Promise<{ success: boolean; error?: string }> => {
    statusMessage.value = '';
    errorMessage.value = '';

    if (!isFormValid.value) {
      const msg = validationErrors.value[0] ?? 'Unable to save goals.';
      errorMessage.value = msg;
      return { success: false, error: msg };
    }

    isSaving.value = true;
    try {
      const goalRequest: CreateGoalRequest = {
        effectiveFromDate: toDateString(effectiveFromDate.value),
        goalType: goalType.value,
        caloriesTarget: calorieTarget.value,
        strategyType: strategyType.value,
        bmrSettings: cloneBmrSettings(bmrSettings.value),
        workoutFuelingTargets: cloneWorkoutFuelingTargets(workoutFuelingTargets.value),
      };

      if (strategyType.value === 'percent') {
        goalRequest.proteinPct = proteinPct.value;
        goalRequest.carbsPct = carbsPct.value;
        goalRequest.fatPct = fatPct.value;
      }

      if (strategyType.value === 'grams_per_kg') {
        if (useScaledMacros.value) {
          goalRequest.proteinGPerKg = gramsPerKgTargets.value.protein ?? undefined;
          goalRequest.carbsGPerKg = gramsPerKgTargets.value.carbs ?? undefined;
          goalRequest.fatGPerKg = gramsPerKgTargets.value.fat ?? undefined;
        } else {
          goalRequest.proteinGPerKg = undefined;
          goalRequest.carbsGPerKg = undefined;
          goalRequest.fatGPerKg = undefined;
          goalRequest.fixedProteinG = parseNumber(fixedProteinG.value) ?? undefined;
          goalRequest.fixedCarbsG = parseNumber(fixedCarbsG.value) ?? undefined;
          goalRequest.fixedFatG = parseNumber(fixedFatG.value) ?? undefined;
        }
      }

      if (strategyType.value === 'flex') {
        goalRequest.fixedProteinG = parseNumber(baseProteinG.value) ?? undefined;
        goalRequest.fixedCarbsG = parseNumber(baseCarbsG.value) ?? undefined;
        goalRequest.fixedFatG = parseNumber(baseFatG.value) ?? undefined;
        goalRequest.flexMacro = flexMacro.value;
      }

      await goalsService.createGoal(goalRequest);
      await Promise.all([loadActiveGoal(), loadGoalSets()]);
      savedSnapshot.value = takeSnapshot();

      // Re-run the diary planner so any loaded day the new goal governs
      // (on/after its effective date) reflects the updated targets instead of
      // staying pinned to stale meal targets. This only runs on a successful
      // save. The diary store is resolved lazily via a dynamic import so we
      // never create a static circular import (diaryStore statically imports
      // this store). Regeneration is best-effort: a planner failure must not
      // fail the goal save.
      try {
        const { useDiaryStore } = await import('@/stores/diaryStore');
        await useDiaryStore().regeneratePlannerForGoalChange(
          goalRequest.effectiveFromDate,
        );
      } catch {
        // Swallow: the goal is already persisted; planner regeneration is a
        // downstream convenience and its failure is surfaced by the planner UI.
      }

      return { success: true };
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unable to save goals.';
      errorMessage.value = msg;
      return { success: false, error: msg };
    } finally {
      isSaving.value = false;
    }
  };

  const createGoal = async (request: CreateGoalRequest): Promise<void> => {
    await goalsService.createGoal(request);
    await Promise.all([loadActiveGoal(), loadGoalSets()]);
  };

  const getChartData = (from?: string, to?: string) =>
    goalsService.getChartData(from, to);

  const initialize = async () => {
    const [settings] = await Promise.all([
      loadProfileSettings(),
      weightStore.ensureLoaded(),
    ]);

    unitSystem.value = settings.units;
    bmrSettings.value = cloneBmrSettings(DEFAULT_GOAL_BMR_SETTINGS);
    workoutFuelingTargets.value = cloneWorkoutFuelingTargets(DEFAULT_WORKOUT_FUELING_TARGETS);
    refreshWeightDerivedState();

    resetMacroDefaults();
    await Promise.all([loadActiveGoal(), loadGoalSets()]);
    if (!activeGoal.value) {
      effectiveFromDate.value = formatDateInput();
    }
    refreshWeightDerivedState();
    savedSnapshot.value = takeSnapshot();
  };

  return {
    unitSystem,
    tdeeCalories,
    activeGoal,
    goals,
    goalsAscending,
    goalSets,
    goalSetsNewestFirst,
    isGoalHistoryLoading,
    effectiveFromDate,
    goalType,
    weeklyWeightChange,
    strategyType,
    proteinPct,
    carbsPct,
    fatPct,
    percentWarning,
    currentWeight,
    useScaledMacros,
    fixedProteinG,
    fixedCarbsG,
    fixedFatG,
    proteinGPerUnit,
    carbsGPerUnit,
    fatGPerUnit,
    baseProteinG,
    baseCarbsG,
    baseFatG,
    flexMacro,
    bmrSettings,
    workoutFuelingTargets,
    effectiveWeightKg,
    isSaving,
    statusMessage,
    errorMessage,
    weightUnitLabel,
    macroUnitLabel,
    calorieTarget,
    macroPercentageTotal,
    percentageValid,
    macroBarSegments,
    currentWeightKg,
    calculatedMacros,
    currentGoalSummary,
    previewSummary,
    previewError,
    validationErrors,
    validationWarnings,
    isFormValid,
    isDirty,
    normalizePercentages,
    resetMacroDefaults,
    setGoalType,
    setStrategyType,
    loadActiveGoal,
    loadGoalsHistory,
    loadGoalSets,
    getGoalForDay,
    saveGoal,
    createGoal,
    updateBmrSettings,
    getChartData,
    initialize,
  };
});
